import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useValidationStore } from "@/state/validationState";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

/** Updating a Project to the latest default: preview, then confirm (#1272). */
export type BaseUpdatePreview = components["schemas"]["BaseUpdatePreview"];
export type BaseUpdateResult = components["schemas"]["BaseUpdateResult"];
export type ConfirmBaseUpdateRequest =
  components["schemas"]["ConfirmBaseUpdateRequest"];

/** Read only, and fresh every time the dialog opens: the answer is what confirm is checked against. */
export function useBaseUpdatePreviewQuery(
  projectId: string,
): UseQueryResult<BaseUpdatePreview> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.baseUpdate(projectId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/base-update", {
          params: { path: { projectId } },
          signal,
        }),
      );
    },
    staleTime: 0,
    gcTime: 0,
  });
}

/** What confirming sends: the preview, exactly as read. Null when there is nothing to confirm. */
export function confirmBodyFrom(
  preview: BaseUpdatePreview,
): ConfirmBaseUpdateRequest | null {
  const { available, blocker, fromSeedVersion, toSeedVersion } = preview;
  if (!available || blocker || !fromSeedVersion || !toSeedVersion) return null;
  return {
    fromSeedVersion,
    toSeedVersion,
    tenantLayerSha256: preview.tenantLayerSha256 ?? null,
    files: preview.files.map(({ path, revision, outcome }) => ({
      path,
      revision,
      outcome,
    })),
  };
}

/** 409: something moved since the preview (or a review opened); nothing was changed. */
export function isBaseUpdateConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * One transaction on the service. Every project read is stale after it: the
 * overview, the list, the files and their histories, the draft and the
 * candidates. A refused confirm re-reads the preview instead.
 */
export function useApplyBaseUpdateMutation(
  projectId: string,
): UseMutationResult<BaseUpdateResult, unknown, ConfirmBaseUpdateRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);
  return useMutation({
    async mutationFn(body) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/base-update", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    onSuccess() {
      // The draft's base moved, so a validation run before it proves nothing.
      clearValidation(projectId);
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError(error) {
      if (isBaseUpdateConflict(error)) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.baseUpdate(projectId),
        });
      }
    },
    retry: false,
  });
}
